import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useForm } from 'react-hook-form';
import api from '../../api/client';
import toast from 'react-hot-toast';
import StatusBadge from '../../components/shared/StatusBadge';
import StaffLink from '../../components/shared/StaffLink';
import {
  PlusIcon, XMarkIcon, LockClosedIcon, EyeIcon, FaceSmileIcon,
} from '@heroicons/react/24/outline';
import { DATE_FLOOR } from '../../lib/dateBounds';

const HAPPINESS = [
  { val: 1, label: 'Never happy' },
  { val: 2, label: 'Occasionally happy' },
  { val: 3, label: 'Usually happy' },
  { val: 4, label: 'Frequently happy' },
  { val: 5, label: 'Always happy' },
];

const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '—';

export default function PerformancePage() {
  const { user, isManagement, isDirector } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [template, setTemplate] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [activeId, setActiveId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [r, s, t] = await Promise.all([
        api.get('/performance/reviews'),
        api.get('/staff'),
        api.get('/performance/template'),
      ]);
      setReviews(r.data); setStaffList(s.data); setTemplate(t.data);
    } catch { toast.error('Failed to load reviews'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createForm = useForm({ defaultValues: { review_type: 'annual', review_date: new Date().toISOString().slice(0, 10) } });
  const create = async (form) => {
    try {
      const period = form.review_period || `${new Date(form.review_date).getFullYear()} Annual`;
      const { data } = await api.post('/performance/reviews', {
        staff_user_id: Number(form.staff_user_id),
        review_type: form.review_type,
        review_period: period,
        review_date: form.review_date,
      });
      toast.success('Review created');
      setShowCreate(false); createForm.reset();
      load();
      setActiveId(data.id);
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;

  return (
    <div className="animate-slide-up">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Performance Reviews</h1>
          <p className="text-gray-500 text-sm">{reviews.length} reviews · BBC self-eval rubric</p>
        </div>
        {isManagement && (
          <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-xl hover:bg-orange-600 font-medium">
            <PlusIcon className="w-5 h-5" /> Start Review
          </button>
        )}
      </div>

      {reviews.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-gray-200">
          <p className="text-gray-500">No reviews yet</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 text-left text-xs font-semibold text-gray-500 uppercase">
                <th className="px-6 py-3">Staff</th>
                <th className="px-6 py-3">Period</th>
                <th className="px-6 py-3">Date</th>
                <th className="px-6 py-3">Reviewer</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Happiness</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {reviews.map(r => (
                <tr key={r.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setActiveId(r.id)}>
                  <td className="px-6 py-3 text-sm font-medium text-gray-900">
                    <StaffLink id={r.staff_user_id} name={r.staff_name} />
                    {r.is_locked && <LockClosedIcon className="w-3 h-3 inline ml-2 text-gray-400" />}
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-600">{r.review_period}</td>
                  <td className="px-6 py-3 text-sm text-gray-600">{formatDate(r.review_date)}</td>
                  <td className="px-6 py-3 text-sm text-gray-600">{r.reviewer_name}</td>
                  <td className="px-6 py-3"><StatusBadge status={r.status} /></td>
                  <td className="px-6 py-3 text-sm">
                    {r.happiness_rating ? (
                      <span className="inline-flex items-center gap-1 text-amber-700">
                        <FaceSmileIcon className="w-4 h-4" /> {r.happiness_rating}/5
                      </span>
                    ) : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-6 py-3 text-right">
                    <EyeIcon className="w-4 h-4 text-gray-400 inline" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <Modal title="Start Performance Review" onClose={() => setShowCreate(false)}>
          <form onSubmit={createForm.handleSubmit(create)} className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Staff *</label>
              <select {...createForm.register('staff_user_id', { required: true })}
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm bg-white focus:border-orange-500 focus:outline-none">
                <option value="">Select…</option>
                {staffList.filter(s => s.id !== user.id).map(s => (
                  <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Review Type *</label>
              <select {...createForm.register('review_type', { required: true })}
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm bg-white focus:border-orange-500 focus:outline-none">
                <option value="annual">Annual</option>
                <option value="90_day">90-day</option>
                <option value="6_month">6-month</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Review Period *</label>
              <input {...createForm.register('review_period')} placeholder="e.g. 2026 Annual"
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Review Date *</label>
              <input type="date" min={DATE_FLOOR} {...createForm.register('review_date', { required: true })}
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none" />
            </div>
            <div className="flex gap-2 pt-2">
              <button type="submit" className="flex-1 bg-orange-500 text-white py-2.5 rounded-xl font-semibold hover:bg-orange-600">Start</button>
              <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2.5 border border-gray-200 rounded-xl text-gray-600">Cancel</button>
            </div>
          </form>
        </Modal>
      )}

      {activeId && <ReviewModal id={activeId} template={template} onClose={() => setActiveId(null)} onChange={load} />}
    </div>
  );
}

function ReviewModal({ id, template, onClose, onChange }) {
  const { user, isManagement, isDirector } = useAuth();
  const [review, setReview] = useState(null);
  const [selfRatings, setSelfRatings] = useState({});
  const [supRatings, setSupRatings] = useState({});
  const [draft, setDraft] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/performance/review/${id}`);
      setReview(data);
      setSelfRatings(data.self_eval_ratings || {});
      setSupRatings(data.supervisor_ratings || {});
      setDraft({
        career_goals: data.career_goals || '',
        personal_goals: data.personal_goals || '',
        support_needed: data.support_needed || '',
        atmosphere_feedback: data.atmosphere_feedback || '',
        happiness_rating: data.happiness_rating || null,
        narrative_summary: data.narrative_summary || '',
        goals_next_period: data.goals_next_period || '',
        development_plan: data.development_plan || '',
      });
    } catch { toast.error('Failed to load review'); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (!review) {
    return <Modal title="Loading…" onClose={onClose}><div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div></Modal>;
  }

  const isStaffMe = review.staff_user_id === user.id;
  const canEdit = !review.is_locked && (isManagement || isStaffMe);

  const save = async () => {
    try {
      await api.patch(`/performance/reviews/${id}`, {
        self_eval_ratings: selfRatings,
        supervisor_ratings: supRatings,
        ...draft,
      });
      toast.success('Saved'); load(); onChange?.();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const sign = async () => {
    try {
      await api.patch(`/performance/reviews/${id}/staff-sign`);
      toast.success('Signed'); load(); onChange?.();
    } catch { toast.error('Failed'); }
  };

  const finalize = async () => {
    try {
      await api.patch(`/performance/reviews/${id}/finalize`);
      toast.success('Finalized & locked'); load(); onChange?.();
    } catch { toast.error('Failed'); }
  };

  return (
    <Modal title={`Performance Review — ${review.staff_name}`} onClose={onClose} maxWidth="max-w-5xl">
      <div className="space-y-5">
        {/* Header */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div><strong>Period:</strong> {review.review_period}</div>
          <div><strong>Date:</strong> {formatDate(review.review_date)}</div>
          <div><strong>Reviewer:</strong> {review.reviewer_name}</div>
          <div><strong>Status:</strong> <StatusBadge status={review.status} /></div>
        </div>

        {/* Rubric — every section + item with self / supervisor parallel ratings */}
        <div className="border border-gray-100 rounded-xl overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 grid grid-cols-12 gap-3 text-xs font-semibold text-gray-600 uppercase">
            <div className="col-span-7">Item</div>
            <div className="col-span-2 text-center">Self (1-5)</div>
            <div className="col-span-3 text-center">Supervisor (1-5)</div>
          </div>
          <div className="divide-y divide-gray-100">
            {template.map(section => (
              <div key={section.id}>
                <div className="bg-orange-50 px-4 py-1.5 text-xs font-bold text-orange-800 uppercase">{section.label}</div>
                {section.items.map(item => (
                  <div key={item.id} className="grid grid-cols-12 gap-3 px-4 py-2 items-center">
                    <div className="col-span-7 text-sm text-gray-700">{item.label}</div>
                    <div className="col-span-2 flex justify-center gap-0.5">
                      {[1, 2, 3, 4, 5].map(n => (
                        <button key={n} type="button" disabled={!canEdit || !isStaffMe}
                          onClick={() => setSelfRatings({ ...selfRatings, [item.item_key]: selfRatings[item.item_key] === n ? null : n })}
                          className={`w-6 h-6 rounded-full text-xs font-medium transition-colors ${
                            selfRatings[item.item_key] === n ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:hover:bg-gray-100'
                          }`}>
                          {n}
                        </button>
                      ))}
                    </div>
                    <div className="col-span-3 flex justify-center gap-0.5">
                      {[1, 2, 3, 4, 5].map(n => (
                        <button key={n} type="button" disabled={!canEdit || !isManagement}
                          onClick={() => setSupRatings({ ...supRatings, [item.item_key]: supRatings[item.item_key] === n ? null : n })}
                          className={`w-6 h-6 rounded-full text-xs font-medium transition-colors ${
                            supRatings[item.item_key] === n ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:hover:bg-gray-100'
                          }`}>
                          {n}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Narrative + dev plan (supervisor) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Narrative Summary</label>
            <textarea value={draft.narrative_summary} disabled={!canEdit || !isManagement}
              onChange={(e) => setDraft({ ...draft, narrative_summary: e.target.value })}
              rows={3} className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none resize-none disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Goals for Next Period</label>
            <textarea value={draft.goals_next_period} disabled={!canEdit || !isManagement}
              onChange={(e) => setDraft({ ...draft, goals_next_period: e.target.value })}
              rows={3} className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none resize-none disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Development Plan</label>
            <textarea value={draft.development_plan} disabled={!canEdit || !isManagement}
              onChange={(e) => setDraft({ ...draft, development_plan: e.target.value })}
              rows={3} className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none resize-none disabled:bg-gray-50" />
          </div>
        </div>

        {/* Goal sheet (employee-driven) */}
        <div className="border border-gray-200 rounded-xl p-4 bg-blue-50/30">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Goal Sheet (Employee)</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Top 3 Career Goals</label>
              <textarea value={draft.career_goals} disabled={!canEdit || !isStaffMe}
                onChange={(e) => setDraft({ ...draft, career_goals: e.target.value })}
                rows={4} placeholder="1.&#10;2.&#10;3."
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm bg-white focus:border-orange-500 focus:outline-none resize-none disabled:bg-gray-50" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Top 3 Personal Goals</label>
              <textarea value={draft.personal_goals} disabled={!canEdit || !isStaffMe}
                onChange={(e) => setDraft({ ...draft, personal_goals: e.target.value })}
                rows={4} placeholder="1.&#10;2.&#10;3."
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm bg-white focus:border-orange-500 focus:outline-none resize-none disabled:bg-gray-50" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">How Can BBC Support You?</label>
              <textarea value={draft.support_needed} disabled={!canEdit || !isStaffMe}
                onChange={(e) => setDraft({ ...draft, support_needed: e.target.value })}
                rows={4} placeholder="What you'd improve on, training needed, what you do well that could be built on..."
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm bg-white focus:border-orange-500 focus:outline-none resize-none disabled:bg-gray-50" />
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Happiness Rating</label>
              <div className="flex flex-wrap gap-1">
                {HAPPINESS.map(h => (
                  <button key={h.val} type="button" disabled={!canEdit || !isStaffMe}
                    onClick={() => setDraft({ ...draft, happiness_rating: draft.happiness_rating === h.val ? null : h.val })}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                      draft.happiness_rating === h.val ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}>
                    {h.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Working Atmosphere Feedback</label>
              <textarea value={draft.atmosphere_feedback} disabled={!canEdit || !isStaffMe}
                onChange={(e) => setDraft({ ...draft, atmosphere_feedback: e.target.value })}
                rows={2} placeholder="What would you change if you could?"
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm bg-white focus:border-orange-500 focus:outline-none resize-none disabled:bg-gray-50" />
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-2 pt-3 border-t border-gray-100">
          {canEdit && (
            <button onClick={save} className="bg-gray-100 text-gray-700 px-4 py-2 rounded-xl text-sm font-medium hover:bg-gray-200">
              Save Draft
            </button>
          )}
          {isStaffMe && review.status === 'submitted' && (
            <button onClick={sign} className="bg-orange-500 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-orange-600">
              Sign Off
            </button>
          )}
          {isDirector && review.status === 'staff_reviewed' && (
            <button onClick={finalize} className="bg-green-500 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-green-600">
              Finalize & Lock
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}

function Modal({ title, onClose, maxWidth = 'max-w-md', children }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`bg-white rounded-2xl shadow-xl w-full ${maxWidth} max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button onClick={onClose}><XMarkIcon className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
